import React, { useState } from 'react'
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as DeliveredIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Pending as PendingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Inventory as MasterAwbIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'

import ShipmentTable from './ShipmentTable'
import dayjs from 'dayjs'
import { shipmentAPI } from '../services/api'
import { apiService } from '../services/apiService'

const getStatusColor = (status, trackingStatus) => {
  const trackingLower = (trackingStatus || '').toLowerCase()
  
  if (trackingLower.includes('delivered')) {
    return 'success'
  } else if (trackingLower.includes('error') || trackingLower.includes('failed')) {
    return 'error'
  } else if (status === 'SHIPPED' || trackingLower.includes('transit')) {
    return 'warning'
  } else {
    return 'default'
  }
}

const getStatusIcon = (status, trackingStatus) => {
  const trackingLower = (trackingStatus || '').toLowerCase()
  
  if (trackingLower.includes('delivered')) {
    return <DeliveredIcon color="success" />
  } else if (trackingLower.includes('error') || trackingLower.includes('failed')) {
    return <ErrorIcon color="error" />
  } else if (status === 'SHIPPED' || trackingLower.includes('transit')) {
    return <WarningIcon color="warning" />
  } else {
    return <PendingIcon color="action" />
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  try {
    return dayjs(dateStr).format('DD.MM.YYYY')
  } catch {
    return dateStr
  }
}

const MasterAWBCard = ({ masterAwb, onRefresh, searchQuery, selectedStatuses = [] }) => {
  const [fedexLoading, setFedexLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const handleFedexRefresh = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setFedexLoading(true)
    try {
      // Use project-specific AWB tracking logic instead of legacy FedEx-only endpoint
      await apiService.updateAwbTracking(masterAwb.project_id)
      // Optionally, you can show a toast/snackbar here
      if (onRefresh) onRefresh()
    } catch (e) {
      // Optionally, show error toast/snackbar
      console.error('AWB tracking update failed', e)
    } finally {
      setFedexLoading(false)
    }
  }

  const handleDelete = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete Master AWB ${masterAwb.master_awb_number} and all its related data? This action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(true)
    try {
      await apiService.deleteMasterAWB(masterAwb.id)
      // Show success message
      alert(`Master AWB ${masterAwb.master_awb_number} deleted successfully!`)
      // Refresh the list
      if (onRefresh) onRefresh()
    } catch (e) {
      console.error('Delete Master AWB failed', e)
      alert(`Failed to delete Master AWB: ${e.message}`)
    } finally {
      setDeleteLoading(false)
    }
  }
  if (!masterAwb || !masterAwb.child_awbs) {
    return null
  }

  // Calculate summary stats for the entire Master AWB
  const totalChildAwbs = masterAwb.child_awbs.length
  const totalShipments = masterAwb.shipments ? masterAwb.shipments.length : 0
  
  const allShipments = masterAwb.shipments || []
  
  // Filter shipments based on search query and selected statuses
  let displayedShipments = allShipments
  
  // Apply search filter
  if (searchQuery) {
    const queryLower = searchQuery.toLowerCase()
    displayedShipments = displayedShipments.filter(shipment => {
      return (
        // Search by order ID
        (shipment.order_id && shipment.order_id.toString().toLowerCase().includes(queryLower)) ||
        // Search by shipment ID
        (shipment.shipment_id && shipment.shipment_id.toString().toLowerCase().includes(queryLower)) ||
        // Search by tracking ID
        (shipment.tracking_id && shipment.tracking_id.toLowerCase().includes(queryLower))
      )
    })
  }
  
  // Apply status filter - show only shipments matching selected statuses
  if (selectedStatuses && selectedStatuses.length > 0) {
    displayedShipments = displayedShipments.filter(shipment => {
      // Handle PENDING status - includes null, empty, or explicit 'PENDING'
      if (selectedStatuses.includes('PENDING') && (!shipment.status || shipment.status === '' || shipment.status === 'PENDING')) {
        return true
      }
      // Handle other statuses
      return selectedStatuses.includes(shipment.status)
    })
  }

  const deliveredCount = allShipments.filter(s => 
    (s.tracking_status || '').toLowerCase().includes('delivered')
  ).length
  const shippedCount = allShipments.filter(s => s.status === 'SHIPPED').length
  const errorCount = allShipments.filter(s => 
    (s.tracking_status || '').toLowerCase().includes('error')
  ).length
  const pendingCount = Math.max(0, totalShipments - (deliveredCount + shippedCount + errorCount));

  // Get overall status for the Master AWB
  const overallStatus = deliveredCount === totalShipments ? 'delivered' :
                       errorCount > 0 ? 'error' :
                       shippedCount > 0 ? 'shipped' : 'pending'

  const statusColors = {
    delivered: 'success',
    error: 'error', 
    shipped: 'warning',
    pending: 'default'
  }

  // Create AWB tracking rows - Master AWB + Child AWBs
  const awbRows = [
    {
      awb: masterAwb.master_awb_number,
      type: 'master',
      shipDate: masterAwb.shipment_creation_date,
      deliverDate: masterAwb.shipment_delivered_date || null, // Real delivery date for master AWB
      status: masterAwb.status ? masterAwb.status.toUpperCase() : 'PENDING', // Real status for master AWB
      trackingStatus: masterAwb.tracking_status || ''
    },
    ...masterAwb.child_awbs.map(childAwb => ({
      awb: childAwb.child_awb_number,
      type: 'child', 
      shipDate: masterAwb.shipment_creation_date,
      deliverDate: childAwb.fedex_delivery_date, // FedEx delivery date
      status: childAwb.fedex_status || 'PENDING', // FedEx tracking status
      trackingStatus: childAwb.fedex_status || ''
    }))
  ]

  return (
    <Card 
      elevation={3} 
      sx={{ 
        mb: 3,
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      <CardHeader
        avatar={<MasterAwbIcon fontSize="large" />}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" component="span">
              Master AWB: {masterAwb.master_awb_number}
            </Typography>
            <Chip
              label={(masterAwb.status ? masterAwb.status.toUpperCase() : 'PENDING')}
              color={statusColors[(masterAwb.status || 'pending').toLowerCase()]}
              size="small"
              icon={getStatusIcon(masterAwb.status || 'pending', masterAwb.tracking_status)}
            />
          </Box>
        }
        subheader={
          <Box>
            <Typography variant="body2" color="text.secondary">
              {totalChildAwbs} Child AWBs • {totalShipments} Total Shipments
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
              AWB Date: {formatDate(masterAwb.shipment_creation_date)}
            </Typography>
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Delete Master AWB and all related data">
              <span>
                <IconButton onClick={handleDelete} size="small" disabled={deleteLoading} color="error">
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh tracking data (ShipStation)">
              <IconButton onClick={e => { e.preventDefault(); e.stopPropagation(); if (onRefresh) onRefresh(e); }} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh AWB Tracking (Master + Child AWBs)">
              <span>
                <IconButton onClick={handleFedexRefresh} size="small" disabled={fedexLoading} color="primary">
                  <ShippingIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={expanded ? "Collapse" : "Expand"}>
              <IconButton onClick={handleExpandClick} size="small">
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      
      <CardContent>
        {/* Always show summary statistics */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`${deliveredCount} Delivered`}
            color="success"
            size="small"
            icon={<DeliveredIcon />}
          />
          <Chip
            label={`${shippedCount} Shipped`}
            color="warning"
            size="small"
            icon={<ShippingIcon />}
          />
          {errorCount > 0 && (
            <Chip
              label={`${errorCount} Errors`}
              color="error"
              size="small"
              icon={<ErrorIcon />}
            />
          )}
          <Chip
            label={`${pendingCount} Pending`}
            color="default"
            size="small"
            icon={<PendingIcon />}
          />
        </Box>

        {/* Collapsible content */}
        <Collapse in={expanded}>
          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
            {/* Left Side - Master AWB Info */}
            <Box sx={{ flex: 1 }}>

            <Typography variant="body2" color="text.secondary">
              {totalChildAwbs} Child AWBs • {totalShipments} Total Shipments
            </Typography>
          </Box>

          {/* Right Side - AWB Tracking Table */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>AWB Tracking</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell><strong>AWB</strong></TableCell>
                    <TableCell><strong>Ship Date</strong></TableCell>
                    <TableCell><strong>Deliver Date</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {awbRows.map((row, index) => (
                    <TableRow key={`${row.type}-${row.awb}`}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="body2" 
                            fontWeight={row.type === 'master' ? 'bold' : 'normal'}
                          >
                            {row.awb}
                          </Typography>
                          {row.type === 'master' && (
                            <Chip label="Master" size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(row.shipDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(row.deliverDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={row.status} 
                          size="small" 
                          color={
                            row.type === 'child' 
                              ? (row.status && row.status.toLowerCase().includes('delivered') ? 'success' : 'default')
                              : statusColors[overallStatus]
                          }
                          variant={row.type === 'child' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
          </Box>
        </Box>

          {/* Shipments View */}
          <Accordion defaultExpanded={true}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Shared Shipments ({searchQuery ? `${displayedShipments.length} of ${allShipments.length}` : allShipments.length} items)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {displayedShipments.length > 0 ? (
                <ShipmentTable 
                  shipments={displayedShipments} 
                  showAwb={false} 
                  onShipmentUpdate={onRefresh}
                />
              ) : searchQuery ? (
                <Typography color="text.secondary">
                  No shipments found matching "{searchQuery}".
                </Typography>
              ) : (
                <Typography color="text.secondary">
                  No shipments found.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Collapse>
      </CardContent>
    </Card>
  )
}

export default React.memo(MasterAWBCard, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.masterAwb.id === nextProps.masterAwb.id &&
    prevProps.masterAwb.updated_at === nextProps.masterAwb.updated_at &&
    prevProps.searchQuery === nextProps.searchQuery &&
    JSON.stringify(prevProps.selectedStatuses) === JSON.stringify(nextProps.selectedStatuses)
  )
})